import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { MapPin, Plus, Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import EventLocationPicker from '@/components/Map/EventLocationPicker';
import { useTranslation } from '@/contexts/TranslationContext';

interface CreateEventModalProps {
  onEventCreated: () => void;
}

export const CreateEventModal = ({ onEventCreated }: CreateEventModalProps) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mapPosition, setMapPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [address, setAddress] = useState('');
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string>('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    end_date: '',
    location_name: '',
    event_type: 'public' as 'public' | 'private',
    max_participants: '',
    registration_deadline: '',
    enable_chat: true,
    enable_checkin: false,
    checkin_radius: 100,
  });

  const getCurrentLocation = async () => {
    try {
      const { getCurrentLocationWithRetry } = await import('@/utils/geolocationUtils');
      const location = await getCurrentLocationWithRetry(2);
      
      if (location) {
        const pos = {
          lat: location.lat,
          lng: location.lng
        };
        setMapPosition(pos);
        toast.success(`Posizione trovata con precisione ${Math.round(location.accuracy || 0)}m!`);
      }
    } catch (error) {
      console.error('Error getting location:', error);
      toast.error('Errore nel rilevamento della posizione');
  };
  };

  const searchAddress = async () => {
    if (!address.trim()) return;
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        setMapPosition({
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon)
        });
        setFormData(prev => ({
          ...prev,
          location_name: result.display_name
        }));
        toast.success('Indirizzo trovato!');
      } else {
        toast.error('Indirizzo non trovato, riprova');
      }
    } catch (error) {
      toast.error('Errore nella ricerca indirizzo');
    }
  };

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadCoverImage = async (): Promise<string | null> => {
    if (!coverImage || !user) return null;

    try {
      const fileExt = coverImage.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `covers/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('event-covers')
        .upload(filePath, coverImage);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('event-covers')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading cover image:', error);
      toast.error('Errore nel caricamento immagine');
      return null;
    }
  };

  const handleMapClick = (lat: number, lng: number) => {
    setMapPosition({ lat, lng });
    setFormData(prev => ({
      ...prev,
      location_name: `Posizione personalizzata (${lat.toFixed(6)}, ${lng.toFixed(6)})`
    }));
    toast.success('Posizione impostata sulla mappa');
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      event_date: '',
      end_date: '',
      location_name: '',
      event_type: 'public',
      max_participants: '',
      registration_deadline: '',
      enable_chat: true,
      enable_checkin: false,
      checkin_radius: 100,
    });
    setMapPosition(null);
    setAddress('');
    setShowMap(false);
    setCoverImage(null);
    setCoverImagePreview('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !mapPosition) {
      toast.error('Seleziona una posizione sulla mappa');
      return;
    }

    setLoading(true);
    try {
      const coverImageUrl = await uploadCoverImage();

      const { error } = await supabase
        .from('events')
        .insert([{
          ...formData,
          latitude: mapPosition.lat,
          longitude: mapPosition.lng,
          location_address: address || formData.location_name,
          cover_image_url: coverImageUrl,
          max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
          user_id: user.id
        }]);

      if (error) throw error;
      
      toast.success('Evento creato con successo!');
      setIsOpen(false);
      onEventCreated();
      resetForm();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Errore nella creazione evento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (open) {
        setTimeout(() => setShowMap(true), 100);
      } else {
        setShowMap(false);
      }
    }}>
      <DialogTrigger asChild>
        <Button className="bg-primary hover:bg-primary/90">
          <Plus className="w-4 h-4 mr-2" />
          {t('events.createNew')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Crea Nuovo Evento</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Titolo *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Es. Passeggiata al parco con cani"
                  required
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Foto di copertina (opzionale)</label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverImageChange}
                  className="mt-1"
                />
                {coverImagePreview && (
                  <div className="mt-2">
                    <img
                      src={coverImagePreview}
                      alt="Anteprima copertina"
                      className="w-full h-32 object-cover rounded-lg"
                    />
                  </div>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium">Descrizione</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descrivi il tuo evento..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Data e ora inizio *</label>
                  <Input
                    type="datetime-local"
                    value={formData.event_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_date: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Fine (opzionale)</label>
                  <Input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Tipo evento</label>
                <Select value={formData.event_type} onValueChange={(value: 'public' | 'private') => setFormData(prev => ({ ...prev, event_type: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">🌍 Pubblico</SelectItem>
                    <SelectItem value="private">🔒 Privato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Max partecipanti</label>
                  <Input
                    type="number"
                    value={formData.max_participants}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_participants: e.target.value }))}
                    placeholder="Illimitato"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Scadenza iscrizioni</label>
                  <Input
                    type="datetime-local"
                    value={formData.registration_deadline}
                    onChange={(e) => setFormData(prev => ({ ...prev, registration_deadline: e.target.value }))}
                  />
                </div>
              </div>
              
              {formData.enable_checkin && (
                <div>
                  <label className="text-sm font-medium">Raggio ritrovo (m)</label>
                  <Input
                    type="number"
                    value={formData.checkin_radius}
                    onChange={(e) => setFormData(prev => ({ ...prev, checkin_radius: parseInt(e.target.value) }))}
                    min="50"
                    max="500"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Serve per abilitare il check-in all'arrivo
                  </p>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Imposta posizione</label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Cerca indirizzo..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), searchAddress())}
                  />
                  <Button type="button" onClick={searchAddress} variant="outline">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
                <Button type="button" onClick={getCurrentLocation} variant="outline" className="w-full mt-2">
                  <MapPin className="w-4 h-4 mr-2" />
                  Usa la mia posizione
                </Button>
              </div>
              
              <div className="h-64 rounded-lg overflow-hidden border">
                {showMap ? (
                  <EventLocationPicker
                    position={mapPosition}
                    onChange={handleMapClick}
                    radius={formData.enable_checkin ? formData.checkin_radius : undefined}
                  />
                ) : (
                  <div className="h-full w-full bg-muted flex items-center justify-center">
                    <div className="text-center">
                      <MapPin className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">Caricamento mappa...</p>
                    </div>
                  </div>
                )}
              </div>
              
              {mapPosition && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium">Posizione impostata:</p>
                  <p className="text-xs text-muted-foreground">
                    {formData.location_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {mapPosition.lat.toFixed(6)}, {mapPosition.lng.toFixed(6)}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex gap-4 pt-4">
            <Button type="submit" disabled={loading || !mapPosition} className="flex-1">
              {loading ? 'Creazione...' : 'Crea Evento'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
              Annulla
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};